export interface AxisCurveConfig {
  min: number;
  max: number;
  center: number;
  deadzonePct: number;
  reverse: boolean;
  expo: number;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const assertConfig = (config: AxisCurveConfig): void => {
  if (
    !Number.isFinite(config.min) ||
    !Number.isFinite(config.max) ||
    !Number.isFinite(config.center) ||
    config.min >= config.center ||
    config.center >= config.max
  ) {
    throw new RangeError('axis curve requires finite min < center < max');
  }
  if (!Number.isFinite(config.deadzonePct) || config.deadzonePct < 0 || config.deadzonePct > 1) {
    throw new RangeError('axis deadzonePct must be in [0, 1]');
  }
  if (!Number.isFinite(config.expo) || config.expo < 0 || config.expo > 1) {
    throw new RangeError('axis expo must be in [0, 1]');
  }
};

const normalise = (raw: number, config: AxisCurveConfig): number => {
  if (!Number.isFinite(raw)) return 0;
  const bounded = clamp(raw, config.min, config.max);
  return bounded >= config.center
    ? (bounded - config.center) / (config.max - config.center)
    : (bounded - config.center) / (config.center - config.min);
};

const removeDeadzone = (value: number, deadzone: number): number => {
  const magnitude = Math.abs(value);
  if (magnitude <= deadzone) return 0;
  return Math.sign(value) * ((magnitude - deadzone) / (1 - deadzone));
};

/**
 * Shapes a bidirectional axis in this exact order: asymmetric range
 * normalisation about center, centred deadzone with remaining-range rescale,
 * cubic expo, then reverse by negation. The result is clamped to [-1, 1].
 */
export const shapeAxis = (raw: number, config: AxisCurveConfig): number => {
  assertConfig(config);
  const normalised = normalise(raw, config);
  const withoutDeadzone = removeDeadzone(normalised, config.deadzonePct);
  const curved = (1 - config.expo) * withoutDeadzone + config.expo * withoutDeadzone ** 3;
  return clamp(config.reverse ? -curved : curved, -1, 1);
};

/**
 * Applies the same centred shaping as shapeAxis and maps it to [0, 1]. Reverse
 * mirrors the unidirectional result (1 - throttle), rather than negating it.
 */
export const shapeThrottle = (raw: number, config: AxisCurveConfig): number => {
  assertConfig(config);
  const normalised = normalise(raw, config);
  const withoutDeadzone = removeDeadzone(normalised, config.deadzonePct);
  const curved = (1 - config.expo) * withoutDeadzone + config.expo * withoutDeadzone ** 3;
  const throttle = clamp((curved + 1) / 2, 0, 1);
  return config.reverse ? 1 - throttle : throttle;
};
